import  {
  type PlatformInsightsOnboardingDistrictDto,
  type PlatformInsightsOnboardingPersonDto,
  type PlatformInsightsOnboardingSchoolDto,
} from "@crowdcoursing/api/openapi";
import { Business as BusinessIcon, Close as CloseIcon } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { makeBubbleAvatarUrl, makeProfileAvatarUrl } from "~utils/url";

export type DistrictOnboardingDetailMetric = "people" | "schools" | "schoolsOnboarded";

export interface DistrictOnboardingDetailDrawerProps {
  district: PlatformInsightsOnboardingDistrictDto | null;
  metric: DistrictOnboardingDetailMetric | null;
  onClose: () => void;
}

const adminSelectHref = (bubbleId: string) => `/admin?schoolAndDistrictSelect=${bubbleId}`;

const personDisplayName = (p: PlatformInsightsOnboardingPersonDto) => {
  const first = p.firstName?.trim() ?? "";
  const last = p.lastName?.trim() ?? "";
  const full = `${first} ${last}`.trim();
  return full || p.email?.trim() || "Unknown";
};

const personAvatarInitial = (p: PlatformInsightsOnboardingPersonDto) => {
  const letter = personDisplayName(p).trim().charAt(0);
  return letter ? letter.toUpperCase() : "?";
};

interface DistrictOnboardingDetailDrawerBodyProps {
  district: PlatformInsightsOnboardingDistrictDto;
  metric: DistrictOnboardingDetailMetric;
  onClose: () => void;
}

function DistrictOnboardingDetailDrawerBody({ district, metric, onClose }: DistrictOnboardingDetailDrawerBodyProps) {
  const districtName = district.name ?? "District";
  const districtBubbleId = district.bubbleId ?? "";
  const schools = district.schools ?? [];
  const onboardedSchools = schools.filter((s) => (s.people?.length ?? 0) > 0);
  const people = district.districtTreePeople ?? [];

  const { list, subtitle, title } = (() => {
    if (metric === "schools") {
      return {
        list: schools,
        subtitle: `${schools.length} school${schools.length === 1 ? "" : "s"} in this district.`,
        title: `All schools — ${districtName}`,
      } as const;
    }
    if (metric === "schoolsOnboarded") {
      return {
        list: onboardedSchools,
        subtitle: `${onboardedSchools.length} school${onboardedSchools.length === 1 ? "" : "s"} with at least one person with a role.`,
        title: `Schools onboarded — ${districtName}`,
      } as const;
    }
    return {
      list: people,
      subtitle: `${people.length} distinct ${people.length === 1 ? "person" : "people"} with roles in this district tree.`,
      title: `People onboarded — ${districtName}`,
    } as const;
  })();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxWidth: { lg: 520, md: "100%", sm: "100%", xs: "100%" },
        minWidth: { lg: 400, md: 0, sm: 0, xs: 0 },
        width: { lg: "44vw", md: "100%", sm: "100%", xs: "100%" },
      }}
    >
      <Box
        sx={{
          alignItems: "flex-start",
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          pb: 2,
          pt: 2,
          px: 2,
        }}
      >
        <Box sx={{ alignItems: "center", display: "flex", gap: 1.5, minWidth: 0 }}>
          <Avatar
            alt=""
            slotProps={{
              img: { "aria-label": `${districtName} district avatar` },
            }}
            src={districtBubbleId ? makeBubbleAvatarUrl(districtBubbleId) : undefined}
            sx={{ height: 56, width: 56 }}
          >
            <BusinessIcon />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h2" sx={{ wordBreak: "break-word" }} variant="h6">
              {title}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {subtitle}
            </Typography>
          </Box>
        </Box>
        <IconButton aria-label="Close details" edge="end" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2, py: 2 }}>
        {list.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            Nothing to show for this selection.
          </Typography>
        ) : metric === "people" ? (
          <Stack divider={<Divider flexItem />} spacing={1.5}>
            {(list as PlatformInsightsOnboardingPersonDto[]).map((p, index) => {
              const uid = p.userId ?? "";
              return (
                <Card key={uid || `${personDisplayName(p)}-${index}`} variant="outlined">
                  <CardContent sx={{ "&:last-child": { pb: 2 }, pt: 2 }}>
                    <Stack alignItems="flex-start" direction="row" spacing={1.5}>
                      <Avatar alt="" src={uid ? makeProfileAvatarUrl(uid) : undefined} sx={{ height: 48, width: 48 }}>
                        {personAvatarInitial(p)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ wordBreak: "break-word" }} variant="subtitle1">
                          {personDisplayName(p)}
                        </Typography>
                        {p.email ? (
                          <Typography color="text.secondary" sx={{ wordBreak: "break-all" }} variant="body2">
                            {p.email}
                          </Typography>
                        ) : null}
                        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                          {(p.roles ?? []).map((role) => (
                            <Chip key={role} label={role} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        ) : (
          <Stack divider={<Divider flexItem />} spacing={1.5}>
            {(list as PlatformInsightsOnboardingSchoolDto[]).map((s, index) => {
              const bid = s.bubbleId ?? "";
              const sid = (s.id ?? bid) || `school-${index}`;
              const peopleCount = s.people?.length ?? 0;
              return (
                <Card key={sid} variant="outlined">
                  <CardContent sx={{ "&:last-child": { pb: 2 }, pt: 2 }}>
                    <Stack alignItems="flex-start" direction="row" spacing={1.5}>
                      <Avatar
                        alt=""
                        slotProps={{
                          img: { "aria-label": `${s.name ?? "School"} school avatar` },
                        }}
                        src={bid ? makeBubbleAvatarUrl(bid) : undefined}
                        sx={{ height: 48, width: 48 }}
                      >
                        <BusinessIcon />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <MuiLink
                          component={RouterLink}
                          sx={{ fontWeight: 600, wordBreak: "break-word" }}
                          to={adminSelectHref(bid)}
                          underline="hover"
                          variant="subtitle1"
                        >
                          {s.name ?? "School"}
                        </MuiLink>
                        <Typography color="text.secondary" variant="body2">
                          {peopleCount} {peopleCount === 1 ? "person" : "people"} with roles in this school subtree
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

export function DistrictOnboardingDetailDrawer({ district, metric, onClose }: DistrictOnboardingDetailDrawerProps) {
  const open = Boolean(district && metric);

  return (
    <Drawer anchor="right" onClose={onClose} open={open}>
      {open && district && metric ? (
        <DistrictOnboardingDetailDrawerBody district={district} metric={metric} onClose={onClose} />
      ) : null}
    </Drawer>
  );
}
